import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardActions, CardContent, Stack, Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { isEmpty } from 'lodash';

import { AppContext } from 'views/App/context';
import { translate as t } from 'hooks/useTranslations';
import { useQueryClient } from 'react-query';
import { createDTB, getDTBLock, updateDTB } from '../_data_sources/manageDTB';
import DTBFormFields from '../_components/DTBFormFields';
import { Form } from 'components/_forms';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { DTBContentBlock, TextContentBlock, VideoContentBlock } from '../../_types/dtb';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import useIssuedManuals from '../../../Policy/IssuedManuals/_data_sources/useIssuedManuals';

const getDefaultContentBlocks = (dtbType: 'CONTENT' | 'VIDEO'): DTBContentBlock[] => {
  if (dtbType === 'VIDEO') {
    const videoBlock: VideoContentBlock = {
      title: 'Training Video',
      contentType: 'VIDEO',
      content: '',
      contentEditable: '',
      contentData: {
        videoUrl: '',
        caption: '',
      },
    };
    return [videoBlock];
  }

  // Default text blocks for CONTENT type
  const textBlocks: TextContentBlock[] = [
    {
      title: 'Scenario',
      contentType: 'CONTENT',
      content: '',
      contentEditable: '',
    },
    {
      title: 'Rule',
      contentType: 'CONTENT',
      content: '',
      contentEditable: '',
    },
    {
      title: 'Analysis',
      contentType: 'CONTENT',
      content: '',
      contentEditable: '',
    },
    {
      title: 'Conclusion',
      contentType: 'CONTENT',
      content: '',
      contentEditable: '',
    },
  ];

  return textBlocks;
};

const DTBCreate: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganizationId: orgId, isLexipolStaff } = useContext(AppContext);

  const [title, setTitle] = useState('');
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [manualId, setManualId] = useState('');
  const [verticalId, setVerticalId] = useState('');
  const [level, setLevel] = useState<'JURISDICTION' | 'GLOBAL' | 'AGENCY'>(isLexipolStaff ? 'GLOBAL' : 'AGENCY');
  const [type, setType] = useState<'CONTENT' | 'VIDEO'>('CONTENT');
  const [isCreating, setIsCreating] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();

  const queryClient = useQueryClient();

  // Fetch issued manuals to get jurisdiction for agency level
  const { data: issuedManualsData } = useIssuedManuals(orgId || '', {}, { enabled: !!orgId });

  // Clear jurisdiction when level changes to GLOBAL
  useEffect(() => {
    if (level === 'GLOBAL') {
      setJurisdictionId('');
    }
  }, [level]);

  // Set jurisdiction when level changes to AGENCY
  useEffect(() => {
    if (level === 'AGENCY' && issuedManualsData?.pages?.[0]?.content?.[0]?.jurisdiction?.id) {
      setJurisdictionId(issuedManualsData.pages[0].content[0].jurisdiction.id);
    }
  }, [level, issuedManualsData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name: title,
      jurisdictionId,
      manualId,
      level,
      verticalId,
      type,
    };
    setIsCreating(true);
    setServerErrors(undefined);
    try {
      await createDTB(
        orgId,
        payload,
        async (response) => {
          // Get lock on the new DTB
          await getDTBLock(orgId, response.id, async () => {
            // Update the DTB with default content blocks based on type
            await updateDTB(
              orgId,
              response.id,
              {
                ...response,
                type,
                contentBlocks: getDefaultContentBlocks(type),
              },
              () => {
                queryClient.invalidateQueries(['dtbs']);
                navigate(`/admin/dtbs/library/${response.id}`);
              },
              (err: ServerErrorResponse) => {
                setIsCreating(false);
                setServerErrors(err);
              }
            );
          });
        },
        (err: ServerErrorResponse) => {
          setIsCreating(false);
          setServerErrors(err);
        }
      );
    } catch (error) {
      console.error('Error creating DTB:', error);
      setIsCreating(false);
    }
  };

  return (
    <TakeoverLayout
      pageTitle={t('dtbs.library.create.title')}
      backUrl='..'
    >
      <Card
        sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
        className='lex-form'
      >
        <Form onSubmit={handleSubmit}>
          <CardContent>
            {errorHelper.renderAllErrors(serverErrors)}
            <DTBFormFields
              orgId={orgId || ''}
              title={title}
              jurisdictionId={jurisdictionId}
              manualId={manualId}
              verticalId={verticalId}
              level={level}
              type={type}
              isNew={true}
              onTitleChange={setTitle}
              onJurisdictionChange={setJurisdictionId}
              onManualChange={setManualId}
              onVerticalChange={setVerticalId}
              onLevelChange={setLevel}
              onTypeChange={setType}
            />
          </CardContent>
          <CardActions>
            <Box />
            <Stack
              direction='row'
              spacing={2}
            >
              <Button
                color='neutral'
                href='..'
              >
                {t('common.cancel')}
              </Button>
              <Button
                disabled={isCreating || isEmpty(title) || (level === 'JURISDICTION' && !jurisdictionId)}
                component='button'
                type='submit'
                variant='contained'
                endIcon={
                  isCreating ? (
                    <CircularProgress
                      size={20}
                      color='inherit'
                    />
                  ) : undefined
                }
              >
                {t('common.save')}
              </Button>
            </Stack>
          </CardActions>
        </Form>
      </Card>
    </TakeoverLayout>
  );
};

export default DTBCreate;
